/**
 * Price sheets (Treatment Plan spec v1.4, S-18 and S-19).
 *
 * Step 1 upload, step 2 check, step 3 changes, step 4 matches, then review and
 * publish. Publishing schedules the sheet: Draft, MCOS review (new products
 * only), Scheduled, Live. Nothing reaches a clinic before its effective date,
 * and a new product changes no clinic's cost until MCOS confirms it.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, Table, Td, Th, inputClass } from '../../components/ui';
import { money, pharmacyApi, shortDate } from '../../lib/pharmacy';

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');

interface Version {
  id: string;
  fileName: string;
  status: 'draft' | 'has_errors' | 'scheduled' | 'live' | 'superseded' | 'withdrawn';
  effectiveDate: string;
  rowCount: number;
  errorCount: number;
  warningCount: number;
  createdAt: string;
  publishedAt: string | null;
  liveFrom: string | null;
}
interface PriceList { id: string; name: string; linkedClinics: number; live: Version | null; versions: Version[] }

const STATUS: Record<Version['status'], { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-[#eef0f3] text-[#4d6072]' },
  has_errors: { label: 'Has errors', cls: 'bg-red-50 text-red-700' },
  scheduled: { label: 'Scheduled', cls: 'bg-[#fbf0d6] text-amber-deep' },
  live: { label: 'Live', cls: 'bg-[#e3efe9] text-forest' },
  superseded: { label: 'Replaced', cls: 'bg-[#eef0f3] text-[#4d6072]' },
  withdrawn: { label: 'Withdrawn', cls: 'bg-[#eef0f3] text-[#4d6072]' },
};
function StatusChip({ status }: { status: Version['status'] }) {
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${STATUS[status].cls}`}>{STATUS[status].label}</span>;
}

const addDays = (days: number) => new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);

function UploadForm({ lists }: { lists: PriceList[] }) {
  const navigate = useNavigate();
  const [listId, setListId] = useState(lists[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(addDays(7));
  const [shipCold, setShipCold] = useState('');
  const [shipStandard, setShipStandard] = useState('');
  const [supplies, setSupplies] = useState<'yes' | 'no'>('yes');
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('effectiveDate', effectiveDate);
      form.append('shipCold', shipCold);
      form.append('shipStandard', shipStandard);
      form.append('suppliesIncluded', supplies);
      return pharmacyApi.post<{ upload: { id: string } }>(`/price-lists/${listId}/uploads`, form);
    },
    onSuccess: (r) => navigate(`/pharmacy/pricing/uploads/${r.upload.id}`),
  });
  const template = useMutation({ mutationFn: () => pharmacyApi.download('/template', 'MCOS_Pharmacy_Price_Sheet_Template_v1.xlsx') });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    upload.mutate();
  };
  return (
    <Card title="Upload a price sheet" subtitle="Step 1. Use the MCOS template, as .xlsx or .csv. One price list per upload." action={<Button size="sm" variant="secondary" loading={template.isPending} onClick={() => template.mutate()}>Download the template</Button>}>
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-3">
        <Field label="Price list">
          <select className={inputClass} value={listId} onChange={(e) => setListId(e.target.value)}>
            {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Effective date" hint={`At least 3 days after publishing, so clinics get notice.`}>
          <input className={inputClass} type="date" min={addDays(3)} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        </Field>
        <Field label="Supplies included" hint="Needles, swabs and similar.">
          <select className={inputClass} value={supplies} onChange={(e) => setSupplies(e.target.value as 'yes' | 'no')}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
        <Field label="Cold shipping per order ($)" hint="Leave empty to use the template's Settings tab.">
          <input className={inputClass} inputMode="decimal" value={shipCold} placeholder="25.76" onChange={(e) => setShipCold(e.target.value)} />
        </Field>
        <Field label="Standard shipping per order ($)">
          <input className={inputClass} inputMode="decimal" value={shipStandard} placeholder="12.86" onChange={(e) => setShipStandard(e.target.value)} />
        </Field>
        <Field label="File">
          <input className={`${inputClass} py-2`} type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <div className="md:col-span-3">
          {upload.error && <p className="mb-3 text-sm text-red-600" role="alert">{errorText(upload.error)}</p>}
          <Button type="submit" loading={upload.isPending} disabled={!file || !listId}>Upload and check</Button>
        </div>
      </form>
    </Card>
  );
}

export function PriceSheetsPage() {
  const qc = useQueryClient();
  const lists = useQuery({ queryKey: ['pharmacy-lists'], queryFn: () => pharmacyApi.get<{ lists: PriceList[] }>('/price-lists') });
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => pharmacyApi.post('/price-lists', { name }),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['pharmacy-lists'] });
      void qc.invalidateQueries({ queryKey: ['pharmacy-me'] });
    },
  });
  if (lists.error) return <ErrorState error={lists.error} retry={() => void lists.refetch()} />;
  if (!lists.data) return <LoadingRows rows={5} />;
  return (
    <div className="space-y-5">
      {lists.data.lists.length > 0 && <UploadForm lists={lists.data.lists} />}
      {lists.data.lists.map((l) => (
        <Card key={l.id} title={l.name} subtitle={`${l.linkedClinics} linked clinic${l.linkedClinics === 1 ? '' : 's'}. ${l.live ? `Live since ${shortDate(l.live.effectiveDate)}.` : 'Nothing live yet.'}`}>
          {l.versions.length === 0 ? (
            <p className="text-[14px] text-[#4d6072]">No versions yet. Upload a price sheet above.</p>
          ) : (
            <Table minWidth={640}>
              <thead><tr><Th>Version</Th><Th>Status</Th><Th>Effective</Th><Th align="right">Products</Th><Th align="right">{''}</Th></tr></thead>
              <tbody>
                {l.versions.map((v) => (
                  <tr key={v.id}>
                    <Td><div className="text-navy">{v.fileName}</div><div className="text-[12px] text-[#4d6072]">uploaded {shortDate(v.createdAt)}</div></Td>
                    <Td><StatusChip status={v.status} /></Td>
                    <Td>{shortDate(v.effectiveDate)}</Td>
                    <Td align="right">{v.rowCount}</Td>
                    <Td align="right"><Link className="text-[14px] font-semibold text-forest hover:underline" to={`/pharmacy/pricing/uploads/${v.id}`}>Open</Link></Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      ))}
      <Card title="Add a price list" subtitle="One pharmacy can keep several, for example Standard and a negotiated account list.">
        <div className="flex max-w-[520px] flex-wrap items-end gap-2">
          <div className="min-w-[220px] flex-1"><Field label="Name"><input className={inputClass} value={name} placeholder="Standard" onChange={(e) => setName(e.target.value)} /></Field></div>
          <Button loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>Add list</Button>
        </div>
        {create.error && <p className="mt-2 text-sm text-red-600">{errorText(create.error)}</p>}
      </Card>
      {lists.data.lists.length === 0 && <EmptyState title="No price lists yet" body="Add a list, then upload its price sheet." />}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One upload: check, changes, matches, review and publish
 * ------------------------------------------------------------------ */

interface Issue { row: number; column: string; message: string }
interface Detail {
  upload: Version & { priceList: { id: string; name: string }; linkedClinics: number; shipColdCents: number; shipStandardCents: number; suppliesIncluded: boolean; confirmedSkus: string[]; warningsAcked: boolean };
  errors: Issue[];
  warnings: Issue[];
  changes: {
    againstLive: { id: string; effectiveDate: string } | null;
    added: Array<{ sku: string; product: string; priceCents: number }>;
    removed: Array<{ sku: string; product: string; priceCents: number }>;
    changed: Array<{ sku: string; product: string; old_cents: number; new_cents: number; pct: number; needs_confirm: boolean }>;
    unchanged: number;
  };
  matches: { known: Array<{ sku: string; product: string; items: string[] }>; mcosWillConfirm: Array<{ sku: string; product: string; items: string[] }>; notMcos: number };
  earliestEffectiveDate: string;
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-forest text-[14px] font-semibold text-white">{n}</span>
        <h2 className="font-display text-[19px] text-navy">{title}</h2>
      </div>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function IssueTable({ issues }: { issues: Issue[] }) {
  return (
    <Table minWidth={520}>
      <thead><tr><Th align="right">Row</Th><Th>Column</Th><Th>What to fix</Th></tr></thead>
      <tbody>{issues.slice(0, 100).map((i, k) => <tr key={k}><Td align="right">{i.row}</Td><Td>{i.column}</Td><Td>{i.message}</Td></tr>)}</tbody>
    </Table>
  );
}

function Timeline({ d }: { d: Detail }) {
  const s = d.upload.status;
  const steps = [
    { label: 'Draft', done: true },
    { label: 'MCOS review', done: d.matches.mcosWillConfirm.length === 0 || s === 'live' || s === 'superseded', note: d.matches.mcosWillConfirm.length ? `${d.matches.mcosWillConfirm.length} new` : 'Nothing new' },
    { label: 'Scheduled', done: ['scheduled', 'live', 'superseded'].includes(s), note: shortDate(d.upload.effectiveDate) },
    { label: 'Live', done: s === 'live' || s === 'superseded' },
  ];
  return (
    <ol className="flex flex-wrap gap-x-2 gap-y-2 text-[13px]" aria-label="Publishing timeline">
      {steps.map((step, i) => (
        <li key={step.label} className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 font-semibold ${step.done ? 'bg-[#e3efe9] text-forest' : 'bg-[#eef0f3] text-[#4d6072]'}`}>
            {step.label}{step.note ? <span className="font-normal">, {step.note}</span> : null}
          </span>
          {i < steps.length - 1 && <span aria-hidden="true" className="text-[#9aa5b1]">&rsaquo;</span>}
        </li>
      ))}
    </ol>
  );
}

export function UploadPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['pharmacy-upload', id], queryFn: () => pharmacyApi.get<Detail>(`/uploads/${id}`) });
  const [acked, setAcked] = useState(false);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const refresh = (data: Detail) => {
    qc.setQueryData(['pharmacy-upload', id], data);
    void qc.invalidateQueries({ queryKey: ['pharmacy-lists'] });
  };
  const publish = useMutation({ mutationFn: () => pharmacyApi.post<Detail>(`/uploads/${id}/publish`, { acknowledgeWarnings: acked, confirmedSkus: confirmed }), onSuccess: refresh });
  const withdraw = useMutation({ mutationFn: () => pharmacyApi.post<Detail>(`/uploads/${id}/withdraw`), onSuccess: refresh });
  const download = useMutation({ mutationFn: () => pharmacyApi.download(`/uploads/${id}/errors.csv`, 'price-sheet-errors.csv') });
  if (detail.error) return <ErrorState error={detail.error} retry={() => void detail.refetch()} />;
  if (!detail.data) return <LoadingRows rows={6} />;
  const d = detail.data;
  const u = d.upload;
  const draft = u.status === 'draft';
  const needConfirm = d.changes.changed.filter((c) => c.needs_confirm);
  const tooSoon = u.effectiveDate < d.earliestEffectiveDate;
  const ready = draft && (!d.warnings.length || acked) && needConfirm.every((c) => confirmed.includes(c.sku)) && !tooSoon;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/pharmacy/pricing" className="text-[14px] text-forest hover:underline">&larr; Price sheets</Link>
          <h1 className="mt-1 font-display text-[28px] text-navy">{u.priceList.name}, effective {shortDate(u.effectiveDate)}</h1>
          <p className="text-[14px] text-[#4d6072]">{u.fileName}, {u.rowCount} products</p>
        </div>
        <StatusChip status={u.status} />
      </div>

      <Step n={2} title="Check">
        {d.errors.length === 0 && d.warnings.length === 0 && <p className="text-[14px] text-forest">Every row passed.</p>}
        {d.errors.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-[14px] font-semibold text-red-700">{d.errors.length} error{d.errors.length === 1 ? '' : 's'}. A sheet with errors cannot be published: fix them in the file and upload it again.</p>
            <IssueTable issues={d.errors} />
          </div>
        )}
        {d.warnings.length > 0 && (
          <div>
            <p className="mb-2 text-[14px] font-semibold text-amber-deep">{d.warnings.length} warning{d.warnings.length === 1 ? '' : 's'} to acknowledge</p>
            <IssueTable issues={d.warnings} />
            {draft && (
              <label className="mt-3 flex items-center gap-2 text-[14px] text-navy">
                <input type="checkbox" className="accent-forest" checked={acked} onChange={(e) => setAcked(e.target.checked)} />
                I've checked each warning and the prices are right
              </label>
            )}
          </div>
        )}
        {(d.errors.length > 0 || d.warnings.length > 0) && (
          <Button className="mt-3" size="sm" variant="secondary" loading={download.isPending} onClick={() => download.mutate()}>Download the list</Button>
        )}
      </Step>

      <Step n={3} title="Changes">
        <p className="text-[14px] text-[#4d6072]">
          {d.changes.againstLive ? `Against the live version from ${shortDate(d.changes.againstLive.effectiveDate)}: ` : 'The first version of this list: '}
          {d.changes.added.length} added, {d.changes.removed.length} removed, {d.changes.changed.length} changed, {d.changes.unchanged} unchanged.
        </p>
        {d.changes.changed.length > 0 && (
          <div className="mt-3">
            <Table minWidth={620}>
              <thead><tr><Th>Product</Th><Th align="right">Was</Th><Th align="right">Now</Th><Th align="right">Change</Th><Th>{needConfirm.length ? 'Confirm' : ''}</Th></tr></thead>
              <tbody>
                {d.changes.changed.map((c) => (
                  <tr key={c.sku}>
                    <Td><div className="text-navy">{c.product}</div><div className="text-[12px] text-[#4d6072]">{c.sku}</div></Td>
                    <Td align="right">{money(c.old_cents)}</Td>
                    <Td align="right">{money(c.new_cents)}</Td>
                    <Td align="right" className={c.needs_confirm ? 'font-semibold text-amber-deep' : ''}>{c.pct > 0 ? '+' : ''}{c.pct}%</Td>
                    <Td>
                      {c.needs_confirm && (draft ? (
                        <label className="flex items-center gap-2 text-[13px]">
                          <input type="checkbox" className="accent-forest" checked={confirmed.includes(c.sku)} onChange={(e) => setConfirmed(e.target.checked ? [...confirmed, c.sku] : confirmed.filter((x) => x !== c.sku))} />
                          This change is right
                        </label>
                      ) : u.confirmedSkus.includes(c.sku) ? <span className="text-[13px] text-forest">Confirmed</span> : null)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {needConfirm.length > 0 && <p className="mt-2 text-[13px] text-[#4d6072]">A change of 30% or more is confirmed line by line.</p>}
          </div>
        )}
        {d.changes.removed.length > 0 && <p className="mt-3 text-[13px] text-[#4d6072]">Removed: {d.changes.removed.map((r) => r.product).join('; ')}</p>}
      </Step>

      <Step n={4} title="Matches">
        <p className="text-[14px] text-navy">{d.matches.known.length} product{d.matches.known.length === 1 ? '' : 's'} MCOS already knows.</p>
        {d.matches.mcosWillConfirm.length > 0 && (
          <div className="mt-3">
            <p className="text-[14px] font-semibold text-navy">New products, MCOS will confirm</p>
            <p className="text-[13px] text-[#4d6072]">MCOS confirms which drug each one is within two business days. Until then they publish but don't change any clinic's cost.</p>
            <ul className="mt-2 divide-y divide-hairline">
              {d.matches.mcosWillConfirm.map((m) => (
                <li key={m.sku} className="flex flex-wrap justify-between gap-2 py-2 text-[14px]">
                  <span className="text-navy">{m.product} <span className="text-[12px] text-[#4d6072]">{m.sku}</span></span>
                  <span className="text-[#4d6072]">Suggested: {m.items.join(', ')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="mt-3 text-[13px] text-[#4d6072]">{d.matches.notMcos} product{d.matches.notMcos === 1 ? ' is' : 's are'} not on the MCOS formulary and stay on your list only.</p>
      </Step>

      <Card title="Review and publish">
        <dl className="grid gap-x-6 gap-y-2 text-[14px] sm:grid-cols-4">
          <div><dt className="text-[#4d6072]">Products</dt><dd className="font-semibold text-navy">{u.rowCount}</dd></div>
          <div><dt className="text-[#4d6072]">Changes</dt><dd className="font-semibold text-navy">{d.changes.added.length + d.changes.removed.length + d.changes.changed.length}</dd></div>
          <div><dt className="text-[#4d6072]">Linked clinics</dt><dd className="font-semibold text-navy">{u.linkedClinics}</dd></div>
          <div><dt className="text-[#4d6072]">Effective</dt><dd className="font-semibold text-navy">{shortDate(u.effectiveDate)}</dd></div>
        </dl>
        <div className="mt-4"><Timeline d={d} /></div>
        {draft && tooSoon && <p className="mt-3 text-sm text-red-600">The effective date must be on or after {shortDate(d.earliestEffectiveDate)} so clinics get 3 days' notice. Upload it again with a later date.</p>}
        {(publish.error || withdraw.error) && <p className="mt-3 text-sm text-red-600" role="alert">{errorText(publish.error ?? withdraw.error)}</p>}
        <div className="mt-4 flex flex-wrap gap-2">
          {draft && <Button loading={publish.isPending} disabled={!ready} onClick={() => publish.mutate()}>Publish</Button>}
          {(draft || u.status === 'scheduled' || u.status === 'has_errors') && <Button variant="secondary" loading={withdraw.isPending} onClick={() => withdraw.mutate()}>Withdraw</Button>}
          {u.status === 'scheduled' && <p className="self-center text-[14px] text-[#4d6072]">Goes live at the start of {shortDate(u.effectiveDate)}, and linked clinics are told now.</p>}
        </div>
      </Card>
    </div>
  );
}
